import React from 'react';
import { CreditCard, Calendar, HelpCircle } from 'lucide-react';

const IncomeParametersSection = ({ formData, handleInputChange, errors, incomeTypes }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {/* Тип дохода */}
      <div className="col-span-full">
        <label htmlFor="incomeType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <CreditCard className="w-4 h-4 mr-2 text-indigo-500" />
          Тип дохода
        </label>
        <select
          id="incomeType"
          name="incomeType"
          value={formData.incomeType}
          onChange={handleInputChange}
          className={`form-select w-full ${errors.incomeType ? 'input-error' : ''}`}
          disabled={formData.isIp || formData.isSelfEmployed}
        >
          {incomeTypes.map((type) => (
            <option key={type.id} value={type.id} title={type.description}>
              {type.name}
            </option>
          ))}
        </select>
        {errors.incomeType && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.incomeType}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {formData.isIp ? 'Для ИП используется тип дохода "от предпринимательской деятельности"' :
          formData.isSelfEmployed ? 'Для самозанятых используется специальный налоговый режим' :
          incomeTypes.find(type => type.id === formData.incomeType)?.description}
        </p>
      </div>
      
      {/* Сумма дохода */}
      <div>
        <label htmlFor="incomeAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <CreditCard className="w-4 h-4 mr-2 text-indigo-500" />
          Сумма дохода
        </label>
        <div className="relative">
          <input
            type="text"
            id="incomeAmount"
            name="incomeAmount"
            value={formData.incomeAmount}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.incomeAmount ? 'input-error' : ''}`}
            placeholder="Введите сумму дохода"
          />
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <span className="text-gray-500 dark:text-gray-400">₽</span>
          </div>
        </div>
        {errors.incomeAmount && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.incomeAmount}
          </p>
        )}
      </div>

      {/* Период дохода */}
      <div>
        <label htmlFor="paymentPeriod" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-indigo-500" />
          Период дохода
        </label>
        <select
          id="paymentPeriod"
          name="paymentPeriod"
          value={formData.paymentPeriod}
          onChange={handleInputChange}
          className={`form-select w-full ${errors.paymentPeriod ? 'input-error' : ''}`}
        >
          <option value="annual">Годовой доход</option>
          <option value="monthly">Ежемесячный доход</option>
          <option value="daily">Дневной доход</option>
        </select>
        {errors.paymentPeriod && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.paymentPeriod}
          </p>
        )}
      </div>

      {/* Год расчета */}
      <div>
        <label htmlFor="year" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-indigo-500" />
          Год расчета
        </label>
        <select
          id="year"
          name="year"
          value={formData.year}
          onChange={handleInputChange}
          className="form-select w-full"
        >
          <option value="2025">2025</option>
          <option value="2024">2024</option>
          <option value="2023">2023</option>
          <option value="2022">2022</option>
          <option value="2021">2021</option>
        </select>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Выберите налоговый период
        </p>
      </div>

      {/* Регион */}
      <div>
        <label htmlFor="region" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <HelpCircle className="w-4 h-4 mr-2 text-indigo-500" />
          Регион
        </label>
        <select
          id="region"
          name="region"
          value={formData.region}
          onChange={handleInputChange}
          className="form-select w-full"
        >
          <option value="moscow">Москва</option>
          <option value="spb">Санкт-Петербург</option>
          <option value="other">Другие регионы</option>
        </select>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Для учета региональных особенностей
        </p>
      </div>

      {/* Примечание к основным параметрам */}
      <div className="col-span-full bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800 mt-2">
        <div className="flex items-start">
          <HelpCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-1" />
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
              Важное о расчете НДФЛ
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              {formData.incomeType === 'salary' 
                ? 'Для доходов от трудовой деятельности свыше 5 млн ₽ в год применяется прогрессивная шкала НДФЛ: 13% до 5 млн ₽ и 15% на сумму превышения.'
                : formData.incomeType === 'dividend'
                ? 'Для дивидендов российских компаний действует ставка 13% (для резидентов) или 15% (для нерезидентов).'
                : formData.incomeType === 'foreign_income'
                ? 'Для доходов из-за рубежа может потребоваться учет соглашений об избежании двойного налогообложения.'
                : 'Для большинства доходов налоговых резидентов РФ применяется ставка 13%, для нерезидентов - 15%.'
              }
              {formData.isIp && ' ИП может применять различные системы налогообложения с разными ставками налога.'}
              {formData.isSelfEmployed && ' Самозанятые уплачивают налог на профессиональный доход по ставке 4% (с физлиц) или 6% (с юрлиц).'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncomeParametersSection;